// ABOUTME: Maps the backend QA-threshold payloads (snake_case, per-sub-mode
// ABOUTME: band arrays) into the shape the FE threshold UI consumes.

// The backend (spec-rite-fastapi) emits resolved categories and a legend in
// snake_case with `bands_by_sub_mode`. The FE reads those keys verbatim — this
// module passes the backend shape through unchanged and only ADDS the derived
// display fields the backend does not send (`overridden_here`, `global_only`,
// `unit_confirmed`) plus the nested `config` the editor edits. Done in one place
// so the rest of the FE is unchanged whether data comes from the mock or the API.

// FE-side display metadata the backend does not send:
// - global_only: category is authored only at global scope (Retro).
// - unit_confirmed: false for the new telemetry categories whose units are not
//   yet finalized (rendered with a ⚠ badge). Update as the backend confirms.
const GLOBAL_ONLY_CATEGORIES = new Set(['retro']);
// Dataless shells with provisional units (no telemetry flows) — kept ⚠ until
// hardware confirms. Per backend Wave 4: manifold_temps + material_app_rate are
// REAL (confirmed units), so they are NOT flagged; only the gun rates + ACB are.
//
// HIDDEN by the backend per Ben & Davis feedback 2026-06-03: left_gun_app_rate,
// right_gun_app_rate, and acb were commented out of the backend
// `qa_thresholds_array` (ACB had no real meaning; gun telemetry isn't hooked up
// yet). The backend no longer returns them, so these keys are currently dead
// lookups. Kept here — not deleted — so the ⚠ flagging restores automatically
// if the backend re-enables them when the telemetry lands.
const UNCONFIRMED_UNIT_CATEGORIES = new Set([
    'left_gun_app_rate',
    'right_gun_app_rate',
    'acb',
]);
// The FE now uses the backend category id verbatim as its key (no translation),
// so the editor, granular band ops, and legend all address a category by the
// exact id the backend stores — nothing can drift. (The former `bead` ↔
// `bead_application_rate` short-key alias was the only divergence and is gone.)
const toFeKey = (id) => id;
const toBeId = (id) => id;

// The scope the editor is authoring at — defines what "overridden here" means.
function authoringScopeOf({ truckId = null, jobId = null }) {
    if (jobId) return 'job';
    if (truckId) return 'truck';
    return 'global';
}

// One backend category → one FE resolved category (plain JS; caller wraps in
// Immutable). The backend's snake_case shape is passed through UNCHANGED — the
// FE reads `id`, `sub_mode`, `sub_modes`, `bands_by_sub_mode`, etc. directly, so
// no field can be silently dropped by a rename map. This adapter only ADDS the
// derived, FE-only fields the backend does not send (`overridden_here`,
// `authored_here`, `global_only`, `unit_confirmed`, `toggleable`) and the nested
// `config` the editor binds its per-category edits to. `source` comes from the
// backend resolver; the derived flags are computed against the authoring scope.
function adaptCategory(beCategory, authoringScope) {
    const key = toFeKey(beCategory.id);
    const source = beCategory.source || 'factory';
    // An override at this scope can be toggled off without losing its values:
    // the backend keeps the bands but marks the override active:false, and the
    // cascade then inherits from the parent (contract C1). Overrides stored
    // before the flag existed default to active:true. So:
    //  - authored_here  = this scope owns an override (active OR retained-off)
    //  - active         = whether that override is currently applying
    //  - overridden_here = the override is authored here AND applying
    const authoredHere = source === authoringScope && source !== 'factory';
    const active = beCategory.active !== false;

    // Whether this metric contributes to the Skyview line color (BR-3/BR-4).
    // Every line-color metric carries an on/off toggle; Retro is independent
    // (BR-10) — it colors its own map dots, not the line, so it has no toggle.
    // Backend `enabled` defaults true when the field is absent.
    const toggleable = !GLOBAL_ONLY_CATEGORIES.has(key);
    const enabled = beCategory.enabled !== false;

    // Pass the whole backend category through, then overlay the FE key (only
    // `bead` differs from its backend id) and the derived flags + config.
    return {
        ...beCategory,
        key,
        global_only: GLOBAL_ONLY_CATEGORIES.has(key),
        unit_confirmed: !UNCONFIRMED_UNIT_CATEGORIES.has(key),
        toggleable,
        enabled,
        source,
        authored_here: authoredHere,
        active,
        overridden_here: authoredHere && active,
        // The editor binds its per-category edits to `config`: the authored
        // fields plus the FE key + active/enabled. Same snake_case as the
        // backend so a save round-trips the exact keys the backend stores.
        config: {
            id: key,
            sub_mode: beCategory.sub_mode || null,
            active,
            enabled,
            bands_by_sub_mode: beCategory.bands_by_sub_mode || {},
            // Percent-margin authoring (contract C8): per sub-mode a value/
            // percent mode, a target the margins measure against, and an
            // independent percent-band set. Absent → value mode / no target.
            value_mode_by_sub_mode: beCategory.value_mode_by_sub_mode || {},
            target_by_sub_mode: beCategory.target_by_sub_mode || {},
            percent_bands_by_sub_mode:
                beCategory.percent_bands_by_sub_mode || {},
        },
    };
}

// Backend GET /get_qa_thresholds `data` (array of categories) → FE resolved
// payload `{ authoringScope, truckId, jobId, categories[] }`. Each backend
// category maps 1:1 to an FE category (the truck-speed / temp / humidity
// categories are shown as their own rail entries — no `other` grouping).
// Global-only categories (Retro) are authored only at global scope, so they
// are dropped from the truck/job rails (the backend returns them at every
// scope; hiding them is a FE concern until retro jobs combine with others).
export function adaptResolved(
    beCategories,
    { truckId = null, jobId = null } = {},
) {
    const authoringScope = authoringScopeOf({ truckId, jobId });
    const list = Array.isArray(beCategories) ? beCategories : [];
    const categories = list
        .map((c) => adaptCategory(c, authoringScope))
        .filter((c) => authoringScope === 'global' || !c.global_only);
    return {
        authoringScope,
        truckId,
        jobId,
        categories,
    };
}

// FE category config (what the editor saves) → backend update payload. The
// backend stores the category by id with its per-sub-mode bands. `enabled`
// carries the per-metric line-color toggle (BR-3): a disabled metric keeps its
// bands but no longer contributes to line color; it still streams its raw value
// to the Skyview details window. Defaults to true when unset. `active` carries
// the override toggle (contract C1): active:false retains the bands but tells
// the cascade to inherit from the parent. Defaults to true when unset.
//
// The backend $sets bands_by_sub_mode per sub-mode (contract C7), so a save for
// one sub-mode must carry ONLY that sub-mode's bands — sending the whole
// per-sub-mode dict would re-write the siblings from a possibly-stale editor
// snapshot and revert them. When a sub-mode is named, scope the payload to it;
// ungrouped categories (no sub_mode) send the single bucket as-is.
export function adaptConfigForSave(feConfig) {
    const subMode = feConfig.sub_mode || null;
    // Scope a per-sub-mode map to just the edited sub-mode (contract C7), so a
    // save can't re-write a sibling from a stale snapshot. Null sub_mode (an
    // ungrouped category) sends the whole map as-is.
    const scope = (map) => {
        const m = map || {};
        return subMode && subMode in m ? { [subMode]: m[subMode] } : m;
    };
    // `min` is no longer authored — the backend derives each band's lower bound
    // from the prior band's max (BE Stage 1, computed-min). `max` is the only
    // authored bound, so drop `min` from outgoing bands rather than send a value
    // the backend ignores (and that could otherwise look authoritative). Every
    // OTHER band field (incl. in_spec) passes through untouched via `...band`.
    const stripMin = (bandMap) => {
        const scoped = scope(bandMap);
        const out = {};
        Object.keys(scoped).forEach((sm) => {
            out[sm] = (scoped[sm] || []).map(({ min, ...band }) => band);
        });
        return out;
    };
    const payload = {
        id: toBeId(feConfig.id),
        sub_mode: subMode,
        enabled: feConfig.enabled !== false,
        active: feConfig.active !== false,
        bands_by_sub_mode: stripMin(feConfig.bands_by_sub_mode),
    };
    // Percent-margin authoring (contract C8): only send these when the config
    // carries them, so existing value-only saves are unchanged. Each is scoped
    // per sub-mode like the bands.
    if (feConfig.value_mode_by_sub_mode) {
        payload.value_mode_by_sub_mode = scope(feConfig.value_mode_by_sub_mode);
    }
    if (feConfig.target_by_sub_mode) {
        payload.target_by_sub_mode = scope(feConfig.target_by_sub_mode);
    }
    if (feConfig.percent_bands_by_sub_mode) {
        payload.percent_bands_by_sub_mode = stripMin(
            feConfig.percent_bands_by_sub_mode,
        );
    }
    return payload;
}

// Backend GET /get_threshold_legend `data` → FE legend. The backend's snake_case
// shape (`category_key`, `sub_modes`) is kept; only `category_key` is translated
// to the FE key (bead). The Skyview filter components read these keys directly.
// `{groups:[{category_key,label,colors:[{color,label,sub_modes}]}]}`
export function adaptLegend(beLegend) {
    const groups = (beLegend && beLegend.groups) || [];
    return {
        groups: groups.map((g) => ({
            ...g,
            category_key: toFeKey(g.category_key),
            colors: (g.colors || []).map((c) => ({
                color: c.color,
                label: c.label,
                sub_modes: c.sub_modes || [],
            })),
        })),
    };
}
